// ── Contractor Rob Flooring — HomePage.js ─────────────────
// Changes in this version:
//  • HeroPriceAd → HeroSalesTag: large red sales tag with fluid typography
//  • WholesaleBanner: corrected &cent; entity for the ¢ symbol
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Droplets,
  Shield,
  Leaf,
  FileText,
  ChevronDown,
  Tag,
  X,
} from "lucide-react";
import { getProducts } from '../services/api';
import DEMO_PRODUCTS from '../data/demoProducts';
import stickerImg from "../assets/99c2.webp";
import productDescImg from '../assets/product-description-01.jpg';
const PRODUCT_DESC_IMG = productDescImg;

const VIGNETTE = 'linear-gradient(135deg, rgba(22,21,18,0.72) 0%, rgba(22,21,18,0.38) 55%, rgba(22,21,18,0.18) 100%)';
const PLACEHOLDER_COLORS = {
  'Southern Oak':   '#d4b896',
  'Maple Forest':   '#c8b89a',
  'Walnut Grove':   '#8b6c4f',
  'Earth Stone':    '#a09080',
  'EIR Collection': '#b8a898',
  'EIR':            '#b8a898',
};
const FALLBACK_BG = ['#d4c4b0', '#8b7c6a', '#b0a898'];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    els.forEach(el => el.setAttribute('data-reveal-init', ''));
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => io.observe(el));
    const safety = setTimeout(() => els.forEach(el => el.classList.add('in')), 1500);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, []);
}

function animateNumber(from, to, duration, cb) {
  const start   = performance.now();
  const isFloat = !Number.isInteger(to);
  const tick = (now) => {
    const t     = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    const v     = from + (to - from) * eased;
    cb(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// ── Wholesale Banner ─────────────────────────────────────────
function WholesaleBanner() {
  return (
    <div
      className="wholesale-banner-mobile-hide"
      style={{
        background: "var(--ink)",
        color: "#fff",
        padding: "10px 0",
        textAlign: "center",
        fontFamily: "var(--sans)",
        fontSize: 13,
        letterSpacing: "0.04em",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
        flexWrap: "wrap",
      }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <Tag size={13} style={{ color: "var(--saffron)", flexShrink: 0 }} />
        <strong style={{ color: "var(--saffron)" }}>
          Wholesale Pricing
        </strong>{" "}
        Starting at 99&cent;/SF
      </span>
      <span style={{ color: "rgba(255,255,255,0.3)" }}>|</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <span style={{ color: "#4A7A5C", fontWeight: 700 }}>✓</span>
        <strong>Free Shipping</strong>
      </span>
    </div>
  );
}

function HeroSalesTag() {
  return (
    <Link
      to="/catalog"
      aria-label="Shop wholesale collections — from 99¢ per sq ft, free shipping"
      style={{
        display: "inline-block",
        textDecoration: "none",
        flexShrink: 0,
        filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.35))",
        transition: "filter 0.18s, transform 0.18s cubic-bezier(.4,0,.2,1)",
        width: "clamp(160px, 22vw, 280px)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px) scale(1.03)";
        e.currentTarget.style.filter =
          "drop-shadow(0 14px 32px rgba(0,0,0,0.5))";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.filter =
          "drop-shadow(0 8px 24px rgba(0,0,0,0.35))";
      }}>
      <img
        src={stickerImg}
        alt="Wholesale pricing from 99¢ per sq ft with free shipping"
        style={{ display: "block", width: "100%", height: "auto" }}
      />
    </Link>
  );
}
// ── Warranty Modal ───────────────────────────────────────────
function WarrantyModal({ onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 500,
        background: "rgba(22,21,18,0.72)",
        backdropFilter: "blur(10px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        overflowY: "auto",
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 720,
          background: "#fff",
          borderRadius: 4,
          boxShadow: "0 40px 100px rgba(0,0,0,.4)",
          overflow: "hidden",
          maxHeight: "90vh",
          overflowY: "auto",
        }}>
        <div style={{ height: 4, background: "var(--ink)" }} />
        <div style={{ padding: "40px 48px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 28,
            }}>
            <div>
              <p
                style={{
                  fontFamily: "var(--sans)",
                  fontSize: 10,
                  letterSpacing: ".22em",
                  textTransform: "uppercase",
                  color: "var(--saffron-deep)",
                  fontWeight: 600,
                  marginBottom: 6,
                }}>
                Love3 Products, Inc.
              </p>
              <h2
                style={{
                  fontFamily: "var(--serif)",
                  fontSize: 28,
                  color: "var(--ink)",
                }}>
                LVP Flooring Warranty
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "var(--bone)",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
              <X size={16} />
            </button>
          </div>

          <p
            style={{
              fontFamily: "var(--sans)",
              fontSize: 13,
              color: "var(--ink-soft)",
              lineHeight: 1.65,
              marginBottom: 20,
            }}>
            This warranty is provided by Love3 Products, Inc., applied for the
            original purchaser and is non-transferable.
          </p>

          <div
            style={{
              padding: "14px 18px",
              background: "rgba(232,179,57,.08)",
              borderRadius: "var(--r-sm)",
              border: "1px solid var(--saffron)",
              marginBottom: 24,
            }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink)", lineHeight: 1.5, fontWeight: 600, marginBottom: 4 }}>Important</p>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.6 }}>
              Love3 Products, Inc. warrants that LVP flooring is free of visual defects for the period stated in this warranty. Customer or installer should carefully inspect each plank before installation. The warranty does not cover any claims related to visible defects once the floor is installed.
            </p>
          </div>

          <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, color: "var(--ink)", marginBottom: 14 }}>What Is Covered</h3>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>a. Manufacture Defects</p>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.6 }}>Love3 Products, Inc warrants the flooring does not delaminate, and is free from manufacture defects including dimension, thickness, and plank stability, following ASTM F3261.</p>
          </div>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>b. Wear Warranty</p>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.6, marginBottom: 12 }}>The wear layer will not wear through for the period stated below. Scratches, indentations, or reduction in gloss level is not considered wear-through.</p>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "var(--sans)" }}>
                <thead>
                  <tr style={{ background: "var(--ink)", color: "#fff" }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Wear Layer Thickness</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }}>0.5mm – 1mm</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }}>0.3 – 0.4mm</th>
                    <th style={{ padding: "10px 12px", textAlign: "center" }}>0.2mm</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: "var(--ivory-soft)" }}>
                    <td style={{ padding: "8px 12px", color: "var(--ash)" }}>Residential</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600, color: "var(--saffron-deep)" }}>25 years</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600, color: "var(--saffron-deep)" }}>10 years</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600, color: "var(--saffron-deep)" }}>5 years</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "8px 12px", color: "var(--ash)" }}>Commercial</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600 }}>10 years</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600 }}>5 years</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600 }}>3 years</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>c. Waterproof</p>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.6 }}>Luxury Vinyl planks are waterproof, and will not buckle or lose integrity when exposed to normal household spills (water, wine, cleanser without harsh chemicals). Household spills must be cleaned up promptly to prevent mold and mildew.</p>
          </div>
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>d. Installation</p>
            <p style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.6, marginBottom: 8 }}>Planks must be installed properly in accordance with Love3 Products, Inc installation instructions. Special notes:</p>
            <ul style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.7, paddingLeft: 20 }}>
              <li>Humidity 30%–55%, temperature 15°C–30°C, pH 7–9; subfloor flatness under 4.8mm within 3m.</li>
              <li>12mm expansion space required along walls and fixed objects.</li>
              <li>Do not fix planks with nails or glue.</li>
              <li>Room length must not exceed 10m (32.8ft).</li>
            </ul>
          </div>

          <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, color: "var(--ink)", marginBottom: 14 }}>What Is Not Covered</h3>
          <ol style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.8, paddingLeft: 20 }}>
            <li>Visible manufacture defects after installation</li>
            <li>Damage caused by accidents, fire, burns, or flooding</li>
            <li>Indentation, scratch and click joint damage by pets, high heels, golf shoes, rolling loads, or wheelchairs without proper floor protectors</li>
            <li>Scratches from moving heavy appliances without felt pad or plywood panel</li>
            <li>Loss of finish gloss or color over time or from direct sunlight exposure</li>
            <li>Damage due to improper installation (non-flat subfloor, insufficient acclimation, use of nails, etc.)</li>
            <li>Flooring installed over concrete without 6-mil moisture barrier</li>
            <li>Radiant heat surface temperature exceeding 85°F (29.8°C)</li>
            <li>Damage from additional underlayment under pre-attached flooring</li>
            <li>Chips, warps, or cracks due to improper expansion spacing or subfloor issues</li>
            <li>Problems from excessive moisture, mildew, alkaline substances, or hydrostatic pressure</li>
            <li>Damage from non-recommended floor care products or strong chemicals</li>
            <li>Flooring installed outdoors or in areas frequently exposed to water</li>
            <li>Improper thickness or dimension selection for intended use</li>
          </ol>

          <div style={{ marginTop: 24, padding: "14px 18px", background: "var(--ivory-soft)", borderRadius: "var(--r-sm)", border: "1px solid var(--rule)" }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--ash)", lineHeight: 1.6, fontStyle: "italic" }}>
              To file a claim, notify your representative with descriptions of the problem along with photos and videos. Love3 Products, Inc. reserves the right to verify defects by having samples for technical analysis or inspection.
            </p>
          </div>
        </div>
        <div style={{ height: 4, background: "var(--saffron)" }} />
      </div>
    </div>
  );
}

// ── Accordion row ────────────────────────────────────────────
function CollectionRow({ product, index, isOpen, onToggle }) {
  const bgImage = product.image_urls?.[0];
  const bgColor = PLACEHOLDER_COLORS[product.name] || "#c8b89a";

  return (
    <div style={{ borderBottom: "1px solid rgba(22,21,18,.12)", overflow: "hidden" }}>
      <button
        onClick={onToggle}
        className="collection-row-btn"
        style={{ height: isOpen ? 220 : 140, transition: "height 0.45s cubic-bezier(.4,0,.2,1)" }}>
        <div
          style={{
            position: "absolute", inset: 0,
            background: bgImage ? `url(${bgImage}) center/cover no-repeat` : bgColor,
            transform: isOpen ? "scale(1.04)" : "scale(1)",
            transition: "transform 0.5s cubic-bezier(.4,0,.2,1)",
          }}
        />
        <div style={{ position: "absolute", inset: 0, background: VIGNETTE }} />
        <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "baseline", gap: 24 }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.22em", color: "rgba(255,255,255,0.55)" }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <div>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(22px, 3.8vw, 48px)", fontWeight: 400, color: "#fff", lineHeight: 1.05, textShadow: "0 2px 12px rgba(22,21,18,0.4)" }}>
              {product.name}
            </h2>
            {product.category && (
              <p style={{ fontFamily: "var(--sans)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)", marginTop: 7 }}>
                {product.category}
              </p>
            )}
          </div>
        </div>
        <ChevronDown
          size={22}
          style={{
            position: "relative", zIndex: 2, color: "rgba(255,255,255,0.75)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.35s cubic-bezier(.4,0,.2,1)", flexShrink: 0,
          }}
        />
      </button>

      <div className="collection-row-body" style={{ maxHeight: isOpen ? 600 : 0 }}>
        <div className="collection-row-body-inner">
          <div>
            <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.75, marginBottom: 22 }}>
              {product.description}
            </p>
            {product.specs && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {product.specs.thickness && <span className="chip">{product.specs.thickness}mm thick</span>}
                {product.specs.wear_layer && <span className="chip">{product.specs.wear_layer} wear layer</span>}
                {product.specs.waterproof && <span className="chip">100% Waterproof</span>}
                {product.specs.surface_texture && <span className="chip">{product.specs.surface_texture}</span>}
                {product.specs.locking_system && <span className="chip">{product.specs.locking_system}</span>}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ash)", marginBottom: 4 }}>
              Interested in this collection?
            </p>
            <Link to={`/products/${product._id}`} className="btn btn-primary btn-lg" style={{ justifyContent: "center", borderRadius: "var(--r-sm)" }}>
              <FileText size={14} /> Request a Price Quote
            </Link>
            <p style={{ fontFamily: "var(--sans)", fontSize: 11, color: "var(--ash)", textAlign: "center", lineHeight: 1.5 }}>
              Wholesale pricing · 1–2 business day response
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function HomePage() {
  const [heroIdx,    setHeroIdx]    = useState(0);
  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [stats,      setStats]      = useState({ proj: 0, sqft: 0, years: 0 });
  const [heroImages, setHeroImages] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);
  const [showWarranty, setShowWarranty] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1000,
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 999px)");
    const h = (e) => setIsNarrow(e.matches);
    mq.addEventListener("change", h);
    setIsNarrow(mq.matches);
    return () => mq.removeEventListener("change", h);
  }, []);

  useReveal();

  useEffect(() => {
    const count = Math.max(heroImages.length, 3);
    const t = setInterval(() => setHeroIdx(i => (i + 1) % count), 5000);
    return () => clearInterval(t);
  }, [heroImages.length]);

  useEffect(() => {
    getProducts()
      .then(res => {
        const prods = res.data.products || res.data || [];
        setProducts(prods);
        const imgs = shuffle(prods.filter(p => p.image_urls?.length).map(p => p.image_urls[0]));
        if (imgs.length) setHeroImages(imgs);
      })
      .catch(() => setProducts(DEMO_PRODUCTS))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const el = document.getElementById('stats-section');
    if (!el) return;
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        animateNumber(0, 1247, 1400, v => setStats(s => ({ ...s, proj: v })));
        animateNumber(0, 4.2,  1400, v => setStats(s => ({ ...s, sqft: v })));
        animateNumber(0, 18,   1400, v => setStats(s => ({ ...s, years: v })));
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const hasImg     = heroImages.length > 0;
  const slideCount = hasImg ? heroImages.length : 3;
  const heroImg    = hasImg ? heroImages[heroIdx % heroImages.length] : null;
  const heroBg     = FALLBACK_BG[heroIdx % FALLBACK_BG.length];
  const toggle = (i) => setOpenIdx((prev) => (prev === i ? null : i));

  return (
    <main style={{ paddingTop: 76 }}>
      <div id="refresh-top" />

      {/* ── WHOLESALE BANNER ── */}
      <WholesaleBanner />

      {/* ── HERO ── */}
      <section
        className="hero"
        style={{ background: heroImg ? "var(--ink)" : heroBg, position: "relative" }}>
        {heroImg && (
          <div
            key={heroImg}
            style={{
              position: "absolute", inset: 0,
              backgroundImage: `url(${heroImg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              animation: "ken-burns 12s ease-in-out infinite alternate",
            }}
          />
        )}
        <div
          style={{
            position: "absolute", inset: 0,
            background: heroImg
              ? "linear-gradient(135deg,rgba(22,21,18,.78) 0%,rgba(22,21,18,.36) 100%)"
              : "linear-gradient(to bottom,rgba(255,255,255,0) 40%,rgba(251,250,246,.7) 100%)",
          }}
        />

        <div
          className="page-container relative flex flex-col justify-center"
          style={{
            zIndex: 2,
            ...(isNarrow && {
              paddingTop: "clamp(56px, 13vh, 176px)",
              paddingBottom: "clamp(40px, 6vh, 80px)",
            }),
          }}>
          <h1
            className="hero-title animate-fade-up delay-1"
            style={{ color: heroImg ? "#fff" : "var(--ink)", maxWidth: 700 }}>
            Premium Luxury Vinyl Flooring Collections
          </h1>
          <p
            className="animate-fade-up delay-2"
            style={{
              fontFamily: "var(--sans)",
              fontSize: 18,
              color: heroImg ? "rgba(255,255,255,.82)" : "var(--ink-soft)",
              marginTop: 24,
              maxWidth: 480,
              lineHeight: 1.55,
            }}>
            We provide quality flooring, and that's a promise you can stand on.
          </p>

          {/* CTA row — large red sales tag */}
          <div
            className="animate-fade-up delay-3"
            style={{
              marginTop: 36,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              width: "100%",
            }}>
            <HeroSalesTag />
          </div>

          <div
            className="animate-fade-up delay-4 flex items-center gap-6"
            style={{ marginTop: 28, flexWrap: "wrap" }}>
            {[
              { Icon: Droplets, text: "100% Waterproof" },
              { Icon: Shield, text: "25 Year Warranty" },
              { Icon: Leaf, text: "FloorScore Certified" },
            ].map(({ Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-2"
                style={{
                  fontFamily: "var(--sans)",
                  fontSize: 12,
                  color: heroImg ? "rgba(255,255,255,.75)" : "var(--ash)",
                  letterSpacing: ".04em",
                }}>
                <Icon size={14} style={{ color: "var(--saffron)" }} />
                {text}
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
            display: "flex", gap: 12, zIndex: 5,
          }}>
          {[...Array(slideCount)].map((_, i) => (
            <button
              key={i}
              onClick={() => setHeroIdx(i)}
              className={`hero-dot${i === heroIdx % slideCount ? " active" : ""}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <section className="marquee">
        <div className="marquee-track">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="marquee-item" style={{ display: "inline-flex", gap: 64 }}>
              <span>FloorScore Certified</span>
              <span>NFPA Class 2</span>
              <span>Unilin Click Lock</span>
              <span>Formaldehyde Free</span>
              <span>Lifetime Residential</span>
              <span>100% Waterproof</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── COLLECTIONS — Accordion ── */}
      <section style={{ padding: "80px 0 0" }}>
        <div className="page-container">
          <div className="flex items-end justify-between" style={{ marginBottom: 48, flexWrap: "wrap", gap: 24 }} data-reveal>
            <div>
              <p className="eyebrow" style={{ marginBottom: 12 }}>
                <span className="divider-mark" style={{ marginRight: 12 }} />
                The Collections
              </p>
              <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(36px,5vw,64px)", maxWidth: 700, textWrap: "balance", lineHeight: 1.06 }}>
                Five distinct{" "}
                <em style={{ fontWeight: 300, color: "var(--saffron-deep)" }}>collections</em>{" "}
                to choose from.
              </h2>
              <p style={{ fontFamily: "var(--sans)", fontSize: 16, color: "var(--ink-soft)", lineHeight: 1.65, maxWidth: 520, marginTop: 16 }}>
                Each engineered around a 100% waterproof SPC core. Click any collection to open it and request wholesale pricing.
              </p>
            </div>
            <Link to="/catalog" className="btn-arrow">View all collections <span>→</span></Link>
          </div>
        </div>

        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          {loading ? (
            <div style={{ padding: "0 48px" }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} className="skeleton" style={{ height: 140, marginBottom: 2, borderRadius: 0 }} />
              ))}
            </div>
          ) : (
            <div style={{ borderTop: "1px solid rgba(22,21,18,.12)" }}>
              {products.map((product, i) => (
                <CollectionRow key={product._id} product={product} index={i} isOpen={openIdx === i} onToggle={() => toggle(i)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── RFQ CTA band ── */}
      {!loading && (
        <section style={{ padding: "64px 0 96px" }}>
          <div className="page-container" style={{ textAlign: "center" }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--ash)", marginBottom: 20, lineHeight: 1.6 }}>
              Ready to price a full project across multiple collections?
            </p>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <Link to="/rfq/submit" className="btn btn-saffron btn-lg" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <FileText size={14} /> Request Wholesale Pricing
              </Link>
              <Link to="/catalog" className="btn btn-outline btn-lg" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                Wholesale Customers <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── PRODUCT DESCRIPTION ── */}
      <section style={{ background: "#fff", padding: "96px 0" }}>
        <div className="page-container">
          <p className="eyebrow" style={{ marginBottom: 12 }}>
            <span className="divider-mark" style={{ marginRight: 12 }} />
            About Our Flooring
          </p>
          <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(32px,4.5vw,52px)", marginBottom: 48, lineHeight: 1.08 }} data-reveal>
            Product Description
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "48px 64px", alignItems: "center" }} data-reveal>
            <div style={{ borderRadius: "var(--r-lg)", overflow: "hidden", boxShadow: "0 16px 48px rgba(22,21,18,.12)" }}>
              <img src={PRODUCT_DESC_IMG} alt="LVP flooring layer diagram showing SPC core construction" style={{ width: "100%", height: "auto", display: "block" }} onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div>
              <p style={{ fontFamily: "var(--sans)", fontSize: 16, color: "var(--ink-soft)", lineHeight: 1.75, marginBottom: 24 }}>
                Our Luxury Vinyl flooring has the newest generation of floor covering features: Enhanced stability, high performance, 100% waterproof, high-density solid core and indentation resistance.
              </p>
              <p style={{ fontFamily: "var(--sans)", fontSize: 16, color: "var(--ink-soft)", lineHeight: 1.75, marginBottom: 40 }}>
                It can be easily installed on a variety of subfloors including concrete or existing vinyl flooring. Our LVP is completely formaldehyde-free and uses components that are safe for residential and commercial installations.
              </p>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, color: "var(--ink)", marginBottom: 20 }}>Layer Description</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {[
                  { name: "Anti-fade UV Coating", desc: "Increased clarity of grains, anti-scratch." },
                  { name: "Transparent Wear Resistant Layer", desc: "Anti-impact and not easily deformed. Durable for more than 20 years." },
                  { name: "Printed Décor Layer", desc: "Modern design based on the most popular trend in the market today." },
                  { name: "Rigid SPC Core", desc: "Composed of CaCO₃ powder and virgin plastic powder." },
                  { name: "Underlayment (Options)", desc: "Increasing adhesion of film and core layer, withstanding temperatures from -10°C to 45°C." },
                  { name: "Waste Segregation & Disposal", desc: "Collected with recyclable plastics and disposed of at recycling plants." },
                ].map((layer) => (
                  <div key={layer.name} style={{ paddingLeft: 14, borderLeft: "2px solid var(--saffron)", paddingBottom: 2 }}>
                    <p style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 2, fontStyle: "italic" }}>{layer.name}</p>
                    <p style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.6 }}>{layer.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRODUCT DETAILS TABLE ── */}
      <section style={{ background: "var(--ivory-soft)", padding: "80px 0" }}>
        <div className="page-container">
          <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(28px,3.5vw,44px)", marginBottom: 36, lineHeight: 1.1 }} data-reveal>Product Details</h2>
          <div style={{ overflowX: "auto" }} data-reveal>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--sans)", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "var(--ink)", color: "#fff" }}>
                  <th style={{ padding: "14px 20px", textAlign: "left", fontWeight: 600, letterSpacing: ".04em", width: "40%" }}>Item</th>
                  <th style={{ padding: "14px 20px", textAlign: "left", fontWeight: 600, letterSpacing: ".04em" }}>Click Lock SPC Flooring</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Size (mm)", "Customized"],
                  ["Thickness", "3.5 – 8.0 mm"],
                  ["Wear Layer", "0.2–0.7 mm / 9 mil–28 mil"],
                  ["Pattern / Color", "As requested"],
                  ["Surface Treatment", "UV Coating"],
                  ["Surface Texture", "Wood, Marble, EIR"],
                  ["Installation", "Unilin Click Lock System"],
                  ["Features", "Waterproof / Slip Resistance / Fire Retardant / Sound Absorbance / Scratch Resistance"],
                  ["Advantages", "Easy Click to Install / Extremely Stability / Eco Friendly"],
                ].map(([item, value], idx) => (
                  <tr key={item} style={{ background: idx % 2 === 0 ? "#fff" : "var(--ivory-soft)", borderBottom: "1px solid var(--rule)" }}>
                    <td style={{ padding: "13px 20px", color: "var(--ash)", fontWeight: 500 }}>{item}</td>
                    <td style={{ padding: "13px 20px", color: "var(--ink)" }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 24 }}>
            <button onClick={() => setShowWarranty(true)} className="btn btn-outline" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Shield size={14} /> View Warranty Information
            </button>
          </div>
        </div>
      </section>

      {/* ── PRODUCT ADVANTAGES ── */}
      <section style={{ background: "#fff", padding: "96px 0" }}>
        <div className="page-container">
          <p style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: "clamp(18px,2.5vw,26px)", color: "var(--ink-soft)", marginBottom: 56, maxWidth: 680, lineHeight: 1.4 }} data-reveal>
            "We provide quality flooring, and that's a promise you can stand on."
          </p>
          <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(28px,4vw,48px)", marginBottom: 48, lineHeight: 1.08 }} data-reveal>
            Product Advantages
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 40 }}>
            {[
              { title: "Waterproof and Damp-proof", body: "As the main component of SPC Flooring is superfine stone powder, it is waterproof which prevents mildew from developing. Thus, you will not have to worry about water and moisture damages to your flooring." },
              { title: "Fire Retardant", body: "The fire rating of our flooring is NFPA CLASS 2. This means that it is difficult for fire to spread in the event of a fire or high temperature situations." },
              { title: "No Formaldehyde", body: "SPC flooring is made from high-class PVC and superfine stone powder, without harmful materials such as benzene, formaldehyde and heavy metal." },
              { title: "Beautiful Appearance", body: "Our products offer high-quality options that have beautiful, natural textures and luxurious appearances with unique patterns and surface textures of natural wood and stone." },
              { title: "Easy to Clean", body: "Our products are stain resistant. If you're worried about pet accidents and child's messes, your best choice would be SPC flooring." },
              { title: "Low Requirement of Subfloor", body: "Fast, easy installation on any surface. SPC flooring has a rigid core and pre-attached underlay which can hide many imperfections of the subfloor." },
            ].map((adv, i) => (
              <div key={adv.title} data-reveal style={{ transitionDelay: `${i * 0.06}s` }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(232,179,57,.12)", color: "var(--saffron)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 18, fontSize: 18 }}>✶</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, color: "var(--ink)", marginBottom: 10 }}>{adv.title}</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.7 }}>{adv.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROMISE ── */}
      <section style={{ background: "var(--ivory-soft)", padding: "64px 0" }}>
        <div className="page-container" style={{ textAlign: "center" }}>
          <span className="eyebrow eyebrow-gold" style={{ display: "inline-block", marginBottom: 16 }}>Our Promise</span>
          <p style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: "clamp(24px,3vw,36px)", color: "var(--ink)", maxWidth: 800, margin: "0 auto", lineHeight: 1.3, textWrap: "balance" }}>
            "We provide quality flooring, and that's a promise you can stand on."
          </p>
          <p style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--ash)", letterSpacing: ".2em", textTransform: "uppercase", marginTop: 16 }}>— Rob, Founder</p>
        </div>
      </section>

      {/* ── CONSTRUCTION ── */}
      <section style={{ background: "var(--ink)", color: "var(--ivory)", padding: "96px 0" }}>
        <div className="page-container">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 48, alignItems: "center", marginBottom: 64 }}>
            <div data-reveal>
              <p className="eyebrow" style={{ color: "var(--saffron)" }}>How it's built</p>
              <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(36px,5vw,60px)", color: "var(--ivory)", marginTop: 16, textWrap: "balance", lineHeight: 1.06 }}>
                Rigid SPC core.{" "}
                <em style={{ color: "var(--saffron)", fontWeight: 300 }}>Five layers</em>{" "}
                of considered engineering.
              </h2>
            </div>
            <p style={{ fontFamily: "var(--sans)", fontSize: 18, color: "rgba(255,255,255,.7)", lineHeight: 1.6 }} data-reveal>
              Every plank is built around a stone-polymer composite core that resists temperature swings, foot traffic, and the kind of moisture that destroys ordinary wood.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 32 }}>
            {[
              { title: "Waterproof Core", body: "SPC rigid core with CaCO₃ powder — completely impervious to moisture, spills, and humidity." },
              { title: "Fire Retardant", body: "NFPA Class 2 rated. Resists fire spread and produces no toxic gases under load." },
              { title: "Click-Lock Install", body: "Unilin Click Lock snaps together cleanly. No glue, no nails — just precision and patience." },
            ].map((f, i) => (
              <div key={f.title} data-reveal style={{ transitionDelay: `${i * 0.08}s` }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(232,179,57,.14)", color: "var(--saffron)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20, fontSize: 18 }}>✶</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 26, color: "var(--ivory)", marginBottom: 10 }}>{f.title}</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "rgba(255,255,255,.65)", lineHeight: 1.6 }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section id="stats-section" style={{ padding: "96px 0" }}>
        <div className="page-container">
          <div className="ornament-rule" style={{ marginBottom: 64 }} data-reveal>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--saffron)", flexShrink: 0 }} />
            <span className="eyebrow">By the numbers</span>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--saffron)", flexShrink: 0 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 32, textAlign: "center" }}>
            {[
              { val: `${stats.proj.toLocaleString()}+`, label: "Projects delivered" },
              { val: `${stats.sqft.toFixed(1)}M ft²`, label: "Floors installed" },
              { val: `${stats.years}yrs`, label: "Of doing one thing well" },
            ].map((s, i) => (
              <div key={s.label} data-reveal style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="stat-num">{s.val}</div>
                <p style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--ash)", letterSpacing: ".18em", textTransform: "uppercase", marginTop: 12 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ background: "var(--ivory-soft)", padding: "96px 0" }}>
        <div className="page-container" style={{ textAlign: "center" }}>
          <p className="eyebrow eyebrow-gold" data-reveal>Begin a project</p>
          <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(40px,6vw,72px)", maxWidth: 800, margin: "16px auto 0", textWrap: "balance", lineHeight: 1.05 }} data-reveal>
            Bring us your{" "}
            <em style={{ fontWeight: 300, color: "var(--saffron-deep)" }}>square footage</em>.
            <br />
            We'll bring back a quiet, considered quote.
          </h2>
          <p style={{ fontFamily: "var(--sans)", fontSize: 18, color: "var(--ink-soft)", maxWidth: 560, margin: "24px auto 0", lineHeight: 1.6 }} data-reveal>
            Trade pricing, sample boxes, and project consultations available on request.
          </p>
          <div className="flex justify-center items-center gap-4" style={{ marginTop: 32, flexWrap: "wrap" }} data-reveal>
            <Link to="/rfq/submit" className="btn btn-saffron btn-lg" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <FileText size={14} /> Request Wholesale Pricing <ArrowRight size={14} />
            </Link>
            <Link to="/catalog" className="btn btn-outline btn-lg" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              Wholesale Customers
            </Link>
          </div>
        </div>
      </section>

      {/* ── WARRANTY MODAL ── */}
      {showWarranty && <WarrantyModal onClose={() => setShowWarranty(false)} />}
    </main>
  );
}
